import { lazy, Suspense, ComponentType } from "react";
import { RouteObject } from "react-router-dom";
import GlobalLoading from "../components/ui/global-loading/GlobalLoading";
import GuestRoute from "../components/guards/GuestRoute";
import ProtectedRoute from "../components/guards/ProtectedRoute";

// Lazy importlar eyni qalır...
// Direct imports for critical path components to improve LCP
import MainRoot from "../components/roots/main-root/MainRoot";
import Home from "../pages/home/Home";

// Lazy imports for other pages
const About = lazy(() => import("../pages/about/About"));
const Outlet = lazy(() => import("../pages/outlet/Outlet"));
const Contact = lazy(() => import("../pages/contact/Contact"));
const Terms = lazy(() => import("../pages/terms/Terms"));
const ProductsPage = lazy(
  () => import("../components/pages/products/ProductsPage")
);
const ProductDetail = lazy(
  () => import("../components/product-detail/ProductDetail")
);
const BasketPage = lazy(() => import("../components/basket/BasketPage"));
const OrderHistory = lazy(
  () => import("../components/pages/order-history/OrderHistory")
);
const OrderAccessoryHistory = lazy(
  () =>
    import("../components/pages/order-accessory-history/OrderAccessoryHistory")
);
const ElectronicApplication = lazy(
  () =>
    import("../components/pages/electronic-application/ElectronicApplication")
);
const AccountInfoComponent = lazy(
  () => import("../components/pages/account-info/AccountInfoComponent")
);
const Career = lazy(() => import("../pages/career/Career"));
const VacancyDetail = lazy(
  () => import("../components/pages/vacancies/vacancyDetail/VacancyDetail")
);
const ServiceOrders = lazy(
  () => import("../pages/serviceOrders/ServiceOrders")
);
const DeliveryTerms = lazy(
  () => import("../pages/delivery-terms/DeliveryTerms")
);
const PrivacyPolicy = lazy(
  () => import("../pages/privacy-policy/PrivacyPolicy")
);
const Campaign = lazy(() => import("../pages/campaigns/Campagin"));
const Campaigns = lazy(() => import("../pages/campaigns/Campaigns"));
const Blog = lazy(() => import("../pages/blog/Blog"));
const BlogDetail = lazy(() => import("../pages/blogDetail/BlogDetail"));
const NewsDetail = lazy(() => import("../pages/newsDetail/newsDetail"));
const VideoTutorials = lazy(
  () => import("../pages/videoTutorials/VideoTutorials")
);
const ServiceSolutions = lazy(
  () => import("../pages/serviceSolutions/ServiceSolutions")
);
const WarrantyTerms = lazy(
  () => import("../pages/warrantyTerms/WarrantyTerms")
);
const Services = lazy(() => import("../pages/services/Services"));
const ItemRepair = lazy(() => import("../pages/services/ItemRepair"));
const Favorites = lazy(() => import("../pages/favorites/Favorites"));
const Repair = lazy(() => import("../components/pages/Repair/Repair"));
const Install = lazy(() => import("../components/pages/Install/Install"));
const LoginScreen = lazy(() => import("../pages/login/LoginScreen"));
const VacancyApply = lazy(() => import("../pages/vacancyAplly/VacancyApply"));
const Exit = lazy(() => import("../pages/exit/Exit"));
const CompleteOrder = lazy(
  () => import("../pages/completeOrder/CompleteOrder")
);
const PaymentSuccess = lazy(() => import("../pages/payment/PaymentSuccess"));
const PaymentError = lazy(() => import("../pages/payment/PaymentError"));
const ForgotPasswordPage = lazy(
  () => import("../pages/forgot-password/ForgotPasswordPage")
);
const ErrorPage = lazy(() => import("../pages/error/ErrorPageClient"));

// ✅ Düzəliş edilmiş Loadable Funksiyası
// Artıq funksiyanı () ilə çağırmaq lazım deyil, birbaşa <Loadable Component={...} /> istifadə edəcəyik.
const Loadable = (Component: ComponentType<any>) => (
  <Suspense fallback={<GlobalLoading />}>
    <Component />
  </Suspense>
);

const ROUTES: RouteObject[] = [
  {
    path: "/",
    element: <MainRoot />,
    children: [
      { path: "", element: <Home /> },
      { path: "outlet", element: Loadable(Outlet) },
      { path: "about", element: Loadable(About) },
      { path: "contact", element: Loadable(Contact) },
      { path: "terms", element: Loadable(Terms) },
      { path: "products", element: Loadable(ProductsPage) },
      { path: "products/:slug", element: Loadable(ProductsPage) },
      { path: "productDetail/:productId", element: Loadable(ProductDetail) },
      { path: "basket", element: Loadable(BasketPage) },
      { path: "orders", element: Loadable(OrderHistory) },
      { path: "accessoryOrders", element: Loadable(OrderAccessoryHistory) },
      { path: "applyService", element: Loadable(ElectronicApplication) },
      {
        path: "accountInfo",
        element: (
          <ProtectedRoute>
            {Loadable(AccountInfoComponent)}
          </ProtectedRoute>
        ),
      },
      { path: "career", element: Loadable(Career) },
      { path: "career/detail/:id", element: Loadable(VacancyDetail) },
      { path: "serviceOrders", element: Loadable(ServiceOrders) },
      { path: "privacy-policy", element: Loadable(PrivacyPolicy) },
      { path: "dasinma-sertleri", element: Loadable(DeliveryTerms) },
      { path: "delivery-terms", element: Loadable(DeliveryTerms) },
      { path: "campaigns", element: Loadable(Campaign) },
      { path: "campaigns/:id", element: Loadable(Campaigns) },
      { path: "blog", element: Loadable(Blog) },
      { path: "blogDetail/:id", element: Loadable(BlogDetail) },
      { path: "newsDetail/:id", element: Loadable(NewsDetail) },
      { path: "videoTutorials", element: Loadable(VideoTutorials) },
      { path: "serviceSolutions", element: Loadable(ServiceSolutions) },
      { path: "warrantyTerms", element: Loadable(WarrantyTerms) },
      { path: "services", element: Loadable(Services) },
      { path: "itemRepair/:id", element: Loadable(ItemRepair) },
      { path: "favourites", element: Loadable(Favorites) },
      { path: "repair", element: Loadable(Repair) },
      { path: "install", element: Loadable(Install) },
      { path: "payment/success", element: Loadable(PaymentSuccess) },
      { path: "payment/error", element: Loadable(PaymentError) },
      { path: "*", element: Loadable(ErrorPage) },
    ],
  },
  {
    element: <GuestRoute />,
    children: [
      { path: "/login", element: Loadable(LoginScreen) },
      { path: "/register", element: Loadable(LoginScreen) },
      { path: "/forgot-password", element: Loadable(ForgotPasswordPage) },
      { path: "/forget-password", element: Loadable(ForgotPasswordPage) },
    ],
  },
  { path: "/career/apply/:id", element: Loadable(VacancyApply) },
  { path: "/exit", element: Loadable(Exit) },
  { path: "/completeOrder", element: Loadable(CompleteOrder) },
  { path: "*", element: Loadable(ErrorPage) },
];

export default ROUTES;
